import { AUDIT_EXPORT_MAX_ROWS, AUDIT_PAGE_SIZE, type AuditLogItem } from '@fos/shared/contracts/audit';
import { Download, History, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDayLong, formatTime, plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Avatar } from '../../../ui/Avatar';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { ListRow } from '../../../ui/ListRow';
import { Mono } from '../../../ui/Mono';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { ClientDot } from '../../clients';
import { TOUCH_BUTTON } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import {
  type AuditFilterState,
  activeFilterCount,
  actorName,
  downloadCsv,
  EMPTY_FILTERS,
  filtersFromParams,
  filtersToInput,
  filtersToParams,
  groupByDay,
} from './audit-ui';
import { AuditEntrySheet } from './components/AuditEntrySheet';
import { AuditFilters } from './components/AuditFilters';

const MOBILE_QUERY = '(max-width: 767px)';

function useIsMobile(): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches;
  const [mobile, setMobile] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setMobile(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return mobile;
}

/**
 * Ustawienia → Audyt (czeka na projekt w Figmie; wzorce: Ustawienia → Zespół, lista z ListRow, panel boczny Sheet).
 * Dziennik zdarzeń workspace'u: kto (osoba, AI, MCP, automat), co, kiedy i przez który kanał; filtry w adresie
 * strony, paginacja kursorem („Pokaż starsze”), szczegóły z diffem przed / po, eksport CSV („Eksportuje dane”).
 * Właściciel i Admin widzą całość, Manager — bez sejfu, zespołu i logowania (baner), Użytkownik — informację.
 */
export function AuditLogPage() {
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const entryId = params.get('wpis');
  const input = useMemo(() => filtersToInput(filters), [filters]);
  const mobile = useIsMobile();
  const allowed = access.isManagerOrAbove;

  const options = trpc.audit.filterOptions.useQuery(undefined, { enabled: allowed, retry: false });
  const list = trpc.audit.list.useInfiniteQuery(
    { ...input, limit: AUDIT_PAGE_SIZE },
    { enabled: allowed, getNextPageParam: (last) => last.nextCursor ?? undefined, retry: false },
  );
  const exportCsv = trpc.audit.exportCsv.useMutation({
    onSuccess: (res) => {
      downloadCsv(res.filename, res.csv);
      toast.success('Pobrano dziennik', {
        description: res.truncated
          ? `Najnowsze ${AUDIT_EXPORT_MAX_ROWS} wpisów — zawęź filtry, żeby pobrać starsze.`
          : `${res.rows} ${plural(res.rows, ['wpis', 'wpisy', 'wpisów'])} w pliku ${res.filename}.`,
      });
    },
    onError: (err) => toast.error('Nie udało się wyeksportować', { description: errorMessage(err) }),
  });

  const setFilters = (next: AuditFilterState) => setParams(filtersToParams(next), { replace: true });
  const openEntry = (id: string | null) => setParams(filtersToParams(filters, id), { replace: true });

  const items: AuditLogItem[] = list.data?.pages.flatMap((p) => p.items) ?? [];
  const forbidden =
    !allowed ||
    (list.isError && appErrorCode(list.error) === 'FORBIDDEN') ||
    (options.isError && appErrorCode(options.error) === 'FORBIDDEN');
  const canExport = options.data?.canExport ?? false;
  const filtered = activeFilterCount(filters) > 0;

  return (
    <div>
      <SettingsHeader
        title="Audyt"
        description="Kto, kiedy i co zmienił — także AI, MCP i automaty. Hasła i sekrety nigdy nie są tu widoczne."
        actions={
          !forbidden && canExport ? (
            <Button
              variant="secondary"
              icon={Download}
              loading={exportCsv.isPending}
              onClick={() => exportCsv.mutate(input)}
              className={TOUCH_BUTTON}
            >
              Eksportuj CSV
            </Button>
          ) : undefined
        }
      />

      {forbidden ? (
        <Section className="mt-6">
          <EmptyState
            icon={ShieldCheck}
            title="Dziennik zdarzeń widzi administrator"
            description="Pełną historię działań w workspace widzą administratorzy, a managerowie — bez sejfu i zespołu."
          />
        </Section>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {options.data?.limited && (
            <ContextBanner tone="info">
              Jako Manager widzisz dziennik bez wpisów sejfu, zespołu i logowania.
            </ContextBanner>
          )}
          <AuditFilters value={filters} onChange={setFilters} options={options.data} />

          <Section>
            {list.isPending ? (
              <ListSkeleton rows={6} />
            ) : list.isError ? (
              <ErrorState onRetry={() => void list.refetch()} retrying={list.isFetching} />
            ) : items.length === 0 ? (
              <EmptyState
                icon={History}
                title={filtered ? 'Brak wpisów dla tych filtrów' : 'Dziennik jest jeszcze pusty'}
                description={
                  filtered ? 'Zmień albo wyczyść filtry.' : 'Tu pojawi się każda zmiana danych, uprawnień i dostępów.'
                }
                action={
                  filtered ? (
                    <Button variant="secondary" onClick={() => setFilters(EMPTY_FILTERS)} className={TOUCH_BUTTON}>
                      Wyczyść filtry
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <div>
                {groupByDay(items).map((group) => (
                  <section key={group.day} aria-label={formatDayLong(group.day)}>
                    <h3 className="pt-4 pb-1 text-caption font-medium text-fg-muted [section:first-child>&]:pt-0">
                      {formatDayLong(group.day)}
                    </h3>
                    <ul className="border-t border-line">
                      {group.items.map((item) => (
                        <li key={item.id}>
                          <AuditRow item={item} onOpen={() => openEntry(item.id)} />
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                {list.hasNextPage && (
                  <div className="pt-3">
                    <Button
                      variant="ghost"
                      size="s"
                      className={TOUCH_BUTTON}
                      loading={list.isFetchingNextPage}
                      onClick={() => void list.fetchNextPage()}
                    >
                      Pokaż starsze
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>
      )}

      <AuditEntrySheet
        entryId={forbidden ? null : entryId}
        onOpenChange={(open) => {
          if (!open) openEntry(null);
        }}
        side={mobile ? 'bottom' : 'right'}
      />
    </div>
  );
}

function AuditRow({ item, onOpen }: { item: AuditLogItem; onOpen: () => void }) {
  const name = actorName(item);
  const channel = item.via === 'ai' ? 'przez AI' : item.via === 'mcp' ? 'przez MCP' : null;
  return (
    <ListRow
      height="l"
      onClick={onOpen}
      leading={
        item.actor ? (
          <Avatar initials={item.actor.initials} color={item.actor.avatarColor} title={item.actor.name} size={32} />
        ) : (
          <Avatar ai={item.actorType === 'ai'} initials="S" title={name} size={32} />
        )
      }
      title={item.actionLabel}
      description={
        <>
          {name}
          {channel ? ` · ${channel}` : ''}
          {item.objectLabel ? ` · ${item.objectLabel}` : ''}
          {item.device && (item.group === 'vault' || item.group === 'auth') ? ` · ${item.device}` : ''}
        </>
      }
      trailing={
        item.client ? (
          <span className="hidden min-w-0 items-center gap-2 text-caption text-fg-secondary sm:inline-flex">
            <ClientDot color={item.client.color} />
            <span className="truncate">{item.client.name}</span>
          </span>
        ) : undefined
      }
      meta={<Mono className="text-caption text-fg-muted">{formatTime(item.at)}</Mono>}
    />
  );
}
